import { type InputHTMLAttributes, forwardRef, useId, type ReactNode } from 'react';

export interface ConsentCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  error?: string;
}

export const ConsentCheckbox = forwardRef<HTMLInputElement, ConsentCheckboxProps>(
  ({ label, error, id, disabled, checked, onChange, className = '', ...props }, ref) => {
    const generatedId = useId();
    const checkboxId = id ?? generatedId;
    const errorId = `${checkboxId}-error`;

    return (
      <div className={`flex flex-col gap-1.5 ${className}`}>
        <label
          htmlFor={checkboxId}
          className={`group flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-all select-none ${
            checked
              ? 'border-brand-orange/50 bg-brand-orange/5'
              : 'border-white/10 bg-navy-light/30 hover:border-white/20 hover:bg-navy-light/50'
          } ${error ? 'border-feedback-danger/80' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          <div className="relative flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              disabled={disabled}
              checked={checked}
              onChange={onChange}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              className="peer sr-only"
              {...props}
            />
            <div
              className={`flex h-5 w-5 items-center justify-center rounded-md border transition-all ${
                checked
                  ? 'border-brand-orange bg-brand-orange text-white'
                  : 'border-white/30 bg-white/5 group-hover:border-white/50'
              } peer-focus-visible:ring-2 peer-focus-visible:ring-brand-orange peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-navy`}
              aria-hidden="true"
            >
              {checked && (
                <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </div>

          <span className="text-xs text-brand-offWhite/90 leading-relaxed sm:text-sm">
            {label}
          </span>
        </label>

        {error && (
          <p id={errorId} role="alert" className="pl-1 text-xs font-medium text-feedback-danger">
            {error}
          </p>
        )}
      </div>
    );
  },
);

ConsentCheckbox.displayName = 'ConsentCheckbox';
